import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, Edit, Trash2, Save, RefreshCw, Eye, Calendar, Tag, Search,
  FileText, Image, Settings, Globe, Clock, CheckCircle, X, Upload, 
  Sparkles, Wand2
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  published: { label: 'Publié', color: 'bg-green-100 text-green-600' },
  scheduled: { label: 'Planifié', color: 'bg-blue-100 text-blue-600' }
};

const CMSBlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, post: null });
  const [categoryModal, setCategoryModal] = useState({ open: false, category: null });
  const [activeTab, setActiveTab] = useState('content');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [seoLoading, setSeoLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [postsRes, catsRes] = await Promise.all([
        api.get('/cms/blog/posts'),
        api.get('/cms/blog/categories')
      ]);
      setPosts(postsRes.data.posts || []);
      setCategories(catsRes.data || []);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // SEO Assistant function
  const handleSeoAssistant = async (action, text = null) => {
    setSeoLoading(true);
    const { post } = editModal;
    const inputText = text || post?.content || post?.excerpt || '';
    
    if (!inputText) {
      toast.error('Veuillez d\'abord ajouter du contenu');
      setSeoLoading(false);
      return;
    }

    try {
      const res = await api.post('/cms/seo-assistant', {
        text: inputText,
        action: action,
        context: post?.title || ''
      });

      const result = res.data.result;

      if (action === 'title') {
        setEditModal(prev => ({
          ...prev,
          post: { ...prev.post, seo_title: result }
        }));
        toast.success('Titre SEO généré !');
      } else if (action === 'description') {
        setEditModal(prev => ({
          ...prev,
          post: { ...prev.post, seo_description: result }
        }));
        toast.success('Meta description générée !');
      } else if (action === 'keywords') {
        const keywords = result.split(',').map(k => k.trim()).filter(k => k);
        setEditModal(prev => ({
          ...prev,
          post: { ...prev.post, seo_keywords: keywords }
        }));
        toast.success('Mots-clés extraits !');
      } else if (action === 'optimize') {
        setEditModal(prev => ({
          ...prev,
          post: { ...prev.post, content: result }
        }));
        toast.success('Contenu optimisé pour le SEO !');
      }
    } catch (error) {
      console.error('SEO Assistant error:', error);
      toast.error('Erreur de l\'assistant SEO');
    } finally {
      setSeoLoading(false);
    }
  };

  const handleSavePost = async () => {
    const { post } = editModal;
    try {
      if (post.id && !post.id.startsWith('new-')) {
        await api.put(`/cms/blog/posts/${post.id}`, post);
      } else {
        await api.post('/cms/blog/posts', post);
      }
      toast.success('Article enregistré');
      loadData();
      setEditModal({ open: false, post: null });
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await api.delete(`/cms/blog/posts/${postId}`);
      toast.success('Article supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSaveCategory = async () => {
    const { category } = categoryModal;
    try {
      if (category.id && !category.id.startsWith('new-')) {
        await api.put(`/cms/blog/categories/${category.id}`, category);
      } else {
        await api.post('/cms/blog/categories', category);
      }
      toast.success('Catégorie enregistrée');
      loadData();
      setCategoryModal({ open: false, category: null });
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'blog');
    
    try {
      const res = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditModal(prev => ({
        ...prev,
        post: { ...prev.post, featured_image: res.data.url }
      }));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur upload');
    }
  };

  const openNewPost = () => {
    setEditModal({
      open: true,
      post: {
        id: `new-${Date.now()}`,
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        featured_image: '',
        category: categories[0]?.id || '',
        tags: [],
        status: 'draft',
        publish_date: '',
        seo_title: '',
        seo_description: '',
        seo_keywords: []
      }
    });
    setActiveTab('content');
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = !searchQuery || 
      post.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || post.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Blog</h1>
          <p className="text-slate-500">{posts.length} articles • {categories.length} catégories</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCategoryModal({ open: true, category: { id: `new-${Date.now()}`, name: '', slug: '', description: '', order: categories.length + 1 } })}>
            <Tag className="w-4 h-4 mr-2" /> Catégories
          </Button>
          <Button onClick={openNewPost} className="bg-gradient-to-r from-cyan-500 to-blue-600">
            <Plus className="w-4 h-4 mr-2" /> Nouvel article
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher un article..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'draft', 'published', 'scheduled'].map(status => (
            <Button
              key={status}
              variant={filterStatus === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus(status)}
            >
              {status === 'all' ? 'Tous' : STATUS_CONFIG[status]?.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Posts Grid */}
      {filteredPosts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Aucun article trouvé</p>
            <Button onClick={openNewPost} className="mt-4">
              <Plus className="w-4 h-4 mr-2" /> Créer un article
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map(post => (
            <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              {/* Featured Image */}
              <div className="aspect-video bg-slate-100 relative">
                {post.featured_image ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${post.featured_image}`}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-12 h-12 text-slate-300" />
                  </div>
                )}
                <Badge className={cn("absolute top-2 right-2", STATUS_CONFIG[post.status]?.color)}>
                  {STATUS_CONFIG[post.status]?.label}
                </Badge>
              </div>
              
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{post.title || 'Sans titre'}</h3>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{post.excerpt}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                  {post.category && (
                    <Badge 
                      variant="secondary"
                      style={{ 
                        backgroundColor: (categories.find(c => c.id === post.category)?.color || '#64748b') + '20',
                        color: categories.find(c => c.id === post.category)?.color || '#64748b'
                      }}
                    >
                      {categories.find(c => c.id === post.category)?.name || post.category}
                    </Badge>
                  )}
                  {post.views > 0 && <span>{post.views} vues</span>}
                  {/* SEO Score */}
                  {post.seo_score !== undefined && (
                    <div className="flex items-center gap-1 ml-auto">
                      <span className="text-xs font-medium">SEO:</span>
                      <div className={cn(
                        "px-1.5 py-0.5 rounded text-xs font-bold",
                        post.seo_score >= 80 ? "bg-green-100 text-green-700" :
                        post.seo_score >= 50 ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      )}>
                        {post.seo_score}%
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-xs text-slate-400">
                    {post.created_at ? new Date(post.created_at).toLocaleDateString('fr-FR') : ''}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditModal({ open: true, post }); setActiveTab('content'); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeletePost(post.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Post Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, post: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editModal.post?.id?.startsWith('new-') ? 'Nouvel article' : 'Modifier l\'article'}
            </DialogTitle>
          </DialogHeader>
          
          {editModal.post && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid grid-cols-3">
                <TabsTrigger value="content"><FileText className="w-4 h-4 mr-2" /> Contenu</TabsTrigger>
                <TabsTrigger value="media"><Image className="w-4 h-4 mr-2" /> Média</TabsTrigger>
                <TabsTrigger value="seo"><Globe className="w-4 h-4 mr-2" /> SEO</TabsTrigger>
              </TabsList>
              
              <div className="flex-1 overflow-y-auto mt-4">
                {/* Content Tab */}
                <TabsContent value="content" className="space-y-4 mt-0">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Titre *</Label>
                      <Input
                        value={editModal.post.title}
                        onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, title: e.target.value } }))}
                        placeholder="Titre de l'article"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Slug (URL)</Label>
                      <Input
                        value={editModal.post.slug}
                        onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, slug: e.target.value } }))}
                        placeholder="mon-article"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Extrait</Label>
                    <Textarea
                      value={editModal.post.excerpt}
                      onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, excerpt: e.target.value } }))}
                      placeholder="Courte description de l'article..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Contenu *</Label>
                    <Textarea
                      value={editModal.post.content}
                      onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, content: e.target.value } }))}
                      placeholder="Contenu de l'article (HTML supporté)..."
                      rows={10}
                      className="font-mono text-sm"
                    />
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Catégorie</Label>
                      <select
                        className="w-full h-10 border rounded-md px-3"
                        value={editModal.post.category}
                        onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, category: e.target.value } }))}
                      >
                        <option value="">Sélectionner...</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Statut</Label>
                      <select
                        className="w-full h-10 border rounded-md px-3"
                        value={editModal.post.status}
                        onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, status: e.target.value } }))}
                      >
                        <option value="draft">Brouillon</option>
                        <option value="published">Publié</option>
                        <option value="scheduled">Planifié</option>
                      </select>
                    </div>
                    {editModal.post.status === 'scheduled' && (
                      <div className="space-y-2">
                        <Label>Date de publication</Label>
                        <Input
                          type="datetime-local"
                          value={editModal.post.publish_date}
                          onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, publish_date: e.target.value } }))}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Tags (séparés par des virgules)</Label>
                    <Input
                      value={(editModal.post.tags || []).join(', ')}
                      onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) } }))}
                      placeholder="marketing, digital, conseils"
                    />
                  </div>
                </TabsContent>
                
                {/* Media Tab */}
                <TabsContent value="media" className="space-y-4 mt-0">
                  <div className="space-y-2">
                    <Label>Image à la une</Label>
                    <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative">
                      {editModal.post.featured_image ? (
                        <>
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${editModal.post.featured_image}`}
                            alt="Featured"
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => setEditModal(prev => ({ ...prev, post: { ...prev.post, featured_image: '' } }))}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-slate-200 transition-colors">
                          <Upload className="w-12 h-12 text-slate-400 mb-2" />
                          <span className="text-slate-500">Cliquer pour uploader</span>
                          <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">Recommandé : 1200×630px (format 16:9)</p>
                  </div>
                </TabsContent>
                
                {/* SEO Tab */}
                <TabsContent value="seo" className="space-y-4 mt-0">
                  {/* AI Assistant Banner */}
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-purple-100 rounded-full">
                        <Sparkles className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Assistant SEO IA</h4>
                        <p className="text-sm text-slate-600">Optimisez automatiquement votre contenu pour le référencement</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleSeoAssistant('title')}
                        disabled={seoLoading}
                        className="bg-white"
                      >
                        {seoLoading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                        Générer titre
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleSeoAssistant('description')}
                        disabled={seoLoading}
                        className="bg-white"
                      >
                        {seoLoading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                        Générer description
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleSeoAssistant('keywords')}
                        disabled={seoLoading}
                        className="bg-white"
                      >
                        {seoLoading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                        Extraire mots-clés
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleSeoAssistant('optimize')}
                        disabled={seoLoading}
                        className="bg-white"
                      >
                        {seoLoading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        Optimiser contenu
                      </Button>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600 mb-4">
                      Ces informations sont utilisées pour optimiser le référencement de votre article sur les moteurs de recherche.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Titre SEO</Label>
                        <Input
                          value={editModal.post.seo_title}
                          onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, seo_title: e.target.value } }))}
                          placeholder="Titre optimisé pour les moteurs de recherche"
                        />
                        <p className="text-xs text-slate-500">{(editModal.post.seo_title || '').length}/60 caractères</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Description SEO</Label>
                        <Textarea
                          value={editModal.post.seo_description}
                          onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, seo_description: e.target.value } }))}
                          placeholder="Description qui apparaîtra dans les résultats de recherche..."
                          rows={3}
                        />
                        <p className="text-xs text-slate-500">{(editModal.post.seo_description || '').length}/160 caractères</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Mots-clés SEO (séparés par des virgules)</Label>
                        <Input
                          value={(editModal.post.seo_keywords || []).join(', ')}
                          onChange={(e) => setEditModal(prev => ({ ...prev, post: { ...prev.post, seo_keywords: e.target.value.split(',').map(t => t.trim()).filter(Boolean) } }))}
                          placeholder="mot-clé 1, mot-clé 2, mot-clé 3"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div className="space-y-2">
                    <Label>Aperçu Google</Label>
                    <div className="p-4 border rounded-lg">
                      <p className="text-blue-600 text-lg hover:underline cursor-pointer">
                        {editModal.post.seo_title || editModal.post.title || 'Titre de l\'article'}
                      </p>
                      <p className="text-green-700 text-sm">
                        www.agence-msc.fr/blog/{editModal.post.slug || 'mon-article'}
                      </p>
                      <p className="text-slate-600 text-sm mt-1">
                        {editModal.post.seo_description || editModal.post.excerpt || 'Description de l\'article...'}
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditModal({ open: false, post: null })}>
              Annuler
            </Button>
            <Button onClick={handleSavePost} className="bg-gradient-to-r from-cyan-500 to-blue-600">
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categories Modal */}
      <Dialog open={categoryModal.open} onOpenChange={(open) => !open && setCategoryModal({ open: false, category: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gérer les catégories</DialogTitle>
          </DialogHeader>
          
          {/* Categories List */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {categories.map(cat => (
              <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: cat.color || '#64748b' }}
                  />
                  <div>
                    <p className="font-medium">{cat.name}</p>
                    <p className="text-xs text-slate-500">{cat.slug}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setCategoryModal({ open: true, category: cat })}>
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          
          {categoryModal.category && (
            <div className="space-y-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>Nom de la catégorie</Label>
                <Input
                  value={categoryModal.category.name}
                  onChange={(e) => setCategoryModal(prev => ({ ...prev, category: { ...prev.category, name: e.target.value } }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={categoryModal.category.description}
                  onChange={(e) => setCategoryModal(prev => ({ ...prev, category: { ...prev.category, description: e.target.value } }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Couleur</Label>
                <div className="flex flex-wrap gap-2">
                  {['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'].map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setCategoryModal(prev => ({ ...prev, category: { ...prev.category, color } }))}
                      className={cn(
                        "w-8 h-8 rounded-full transition-all border-2",
                        categoryModal.category.color === color ? "border-slate-400 ring-2 ring-offset-2 ring-slate-300" : "border-transparent"
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryModal({ open: false, category: null })}>
              Fermer
            </Button>
            {categoryModal.category && (
              <Button onClick={handleSaveCategory}>
                <Save className="w-4 h-4 mr-2" /> Enregistrer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSBlogPage;
